import React, { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Users } from "lucide-react";

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

/**
 * Public referral leaderboard — shown on About page as social proof.
 * Displays top 10 referrers with anonymized names + referral counts.
 */
export default function ReferralLeaderboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referral/leaderboard")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!items.length) return null;  // hide entirely if no data yet

  return (
    <section className="py-20 lg:py-28 bg-primary text-white relative overflow-hidden" data-testid="referral-leaderboard-section">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
      <div className="container-x relative">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 text-accent">
            <Trophy className="h-4 w-4" />
            <p className="text-xs uppercase tracking-[0.3em]">Duta Batik Travel</p>
          </div>
          <h2 className="mt-3 font-serif text-4xl lg:text-5xl text-white tracking-tight">
            Alumni Jamaah yang <span className="text-accent italic">Merekomendasikan</span> Kami
          </h2>
          <p className="mt-5 text-white/70 leading-relaxed">
            Terima kasih kepada duta Batik Travel yang telah mempercayakan sanak keluarga & sahabat mereka. Bergabunglah — dapatkan bonus Rp 500.000 untuk setiap referral yang berangkat.
          </p>
        </div>

        {/* Top 3 podium */}
        {items.length >= 3 && (
          <div className="grid grid-cols-3 gap-3 max-w-3xl mx-auto mb-10">
            {[items[1], items[0], items[2]].map((r, idx) => {
              const rank = idx === 0 ? 2 : idx === 1 ? 1 : 3;
              const heights = { 1: "h-40", 2: "h-32", 3: "h-24" };
              return (
                <div key={rank} className="flex flex-col items-center" data-testid={`podium-${rank}`}>
                  <div className={`relative aspect-square w-full max-w-[120px] mb-3 rounded-full overflow-hidden border-2 ${rank === 1 ? "border-accent" : "border-white/30"} bg-white/5`}>
                    {r.picture ? (
                      <img src={r.picture} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-2xl font-serif text-accent">{r.display_name?.[0] || "?"}</div>
                    )}
                    <span className={`absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full grid place-items-center text-sm font-bold ${rank === 1 ? "bg-accent text-primary" : "bg-white/20 text-white"}`}>
                      {rank}
                    </span>
                  </div>
                  <p className="font-serif text-lg text-white text-center leading-tight">{r.display_name}</p>
                  <p className="text-accent text-sm mt-1">{r.referrals} referral</p>
                  <div className={`mt-3 ${heights[rank]} w-full rounded-t-md bg-gradient-to-t ${rank === 1 ? "from-accent/40 to-accent/10" : "from-white/10 to-white/5"} border-t-2 ${rank === 1 ? "border-accent" : "border-white/30"}`} />
                </div>
              );
            })}
          </div>
        )}

        {/* Rest of the leaderboard */}
        {items.length > 3 && (
          <div className="max-w-2xl mx-auto bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            {items.slice(3).map((r, i) => (
              <div key={i + 3} className="flex items-center gap-4 p-4 border-b border-white/5 last:border-b-0" data-testid={`row-${i + 4}`}>
                <span className="text-white/40 font-mono text-sm w-5">{i + 4}</span>
                <div className="h-9 w-9 rounded-full bg-white/10 border border-white/20 overflow-hidden shrink-0">
                  {r.picture ? (
                    <img src={r.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-xs text-accent">{r.display_name?.[0] || "?"}</div>
                  )}
                </div>
                <span className="text-white flex-1">{r.display_name}</span>
                <span className="text-accent text-sm inline-flex items-center gap-1">
                  <Users className="h-3 w-3" /> {r.referrals}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* If only 1-2 entries, show them simply */}
        {items.length < 3 && (
          <div className="max-w-lg mx-auto space-y-3">
            {items.map((r, i) => (
              <div key={i} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg p-4" data-testid={`solo-${i}`}>
                <span className="text-accent font-mono text-sm">#{i + 1}</span>
                <div className="h-10 w-10 rounded-full bg-white/10 border border-white/20 overflow-hidden shrink-0">
                  {r.picture ? <img src={r.picture} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-accent">{r.display_name?.[0]}</div>}
                </div>
                <span className="text-white flex-1">{r.display_name}</span>
                <span className="text-accent text-sm">{r.referrals} referral</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
